import {addDisposer, Instance, types} from 'mobx-state-tree';
import {reaction} from "mobx";

import {
    ItemModelSnapshotOut, ItemModelType,
    WorkspaceModelSnapshotIn,
} from './WorkspaceStore';


import {escapeRegExp} from "../utils/escapeRegExp";

import {Client} from "../API";


import {SearchStore} from "./SearchStore";
import {ViewStore} from "./ViewStore";


export const DEFAULT_LEFT_MENU_WIDTH = 342;


export type RightMenuName = 'outline' | 'comments' | 'history' | 'activeUsers' | 'acknowledgement' | '';

export interface SearchInTitleResult {
    item: ItemModelType;
    highlight: string;
}

const RIGHT_BAR_DEFAULT_WIDTH = 420;

const STORAGE_NAME_FOR_PINNED_MENU = 'menu-pinned';
const WORKSPACE_MIN_WIDTH = 800;
const STORAGE_NAME_FOR_SIDEBAR_COLLAPSE = 'main-sidebar-collapsed';

export const RootStore = types
    .model({
        darkTheme: types.optional(types.boolean, false),
        currentLocale: types.optional(types.string, 'en-US'),
        leftMenuWidth: types.optional(types.number, DEFAULT_LEFT_MENU_WIDTH),
        rightMenu: types.optional(types.string, ''),
        rightMenuWidth: types.optional(types.number, RIGHT_BAR_DEFAULT_WIDTH),
        isRightMenuPinned: types.optional(types.boolean, false),
        isSidebarCollapsed: types.optional(types.boolean, false),
        client: types.optional(types.maybeNull(types.frozen<Client>()), null),
        search: types.optional(SearchStore, {}),
        view: types.optional(ViewStore, {}),
        clusterFilter: types.optional(types.string, ''),
        isTreeCollapsed: types.optional(types.boolean, false),
        isReady: types.optional(types.boolean, false),
        isOnline: types.optional(types.boolean, true),
    })
    .views(self => ({
        get minimumContentWidth(): number {
            return (self.rightMenu !== '' ? self.rightMenuWidth : 40) + WORKSPACE_MIN_WIDTH;
        },
        getAvailableClusters(item: ItemModelType): ItemModelSnapshotOut[] {
            if (!self.view.workspace) {
                return [];
            }
            const excludeIds = [...item.parentIds, item.id, ...item.childIds];
            return Object.values(self.view.workspace.items.toJSON())
                .filter(item => (item.type === 'CLUSTER' && !item.trashed && !item.archived && (excludeIds.indexOf(item.id) === -1)))
                .filter(item => {
                    if (self.clusterFilter.length > 0) {
                        return item.name.toLowerCase().indexOf(self.clusterFilter.toLowerCase()) > -1
                    } else {
                        return true;
                    }
                });
        },
        get searchTitleMatches(): SearchInTitleResult[] {
            if (self.search.isEmpty) {
                return [];
            }
            const activeWorkspace = self.view.workspace!;
            const searchRegExp = new RegExp(escapeRegExp(self.search.textInput), 'i');
            const matchResults = [];
            for (const item of activeWorkspace.items.values()) {
                let passCondition: boolean;
                switch (activeWorkspace.view) {
                    case 'main':
                    case 'recent':
                    case 'active':
                        passCondition = !item.trashed && !item.archived;
                        break;
                    case 'archive':
                        passCondition = !item.trashed && item.archived;
                        break;
                    case 'trash':
                        passCondition = item.trashed;
                        break;
                    default:
                        throw new Error('view type is not supported: ' + activeWorkspace.view);
                }
                const searchMatch = item.name.match(searchRegExp);
                if (passCondition && searchMatch !== null) {
                    matchResults.push({
                        item: item,
                        highlight: searchMatch[0]
                    });
                }
            }
            return matchResults;
        },
    }))
    .actions(self => ({
        toggleIsRightMenuPinned(isRightMenuPinned: boolean = !self.isRightMenuPinned) {
            self.isRightMenuPinned = isRightMenuPinned;
        },
        readFromLocalStorage() {
            let isMenuPinned = window.localStorage.getItem(STORAGE_NAME_FOR_PINNED_MENU);
            if (isMenuPinned) {
                self.isRightMenuPinned = JSON.parse(isMenuPinned);
            }
            const isSidebarCollapsed = window.localStorage.getItem(STORAGE_NAME_FOR_SIDEBAR_COLLAPSE);
            if (isSidebarCollapsed) {
                self.isSidebarCollapsed = JSON.parse(isSidebarCollapsed);
            }
        },
    }))
    .actions(self => ({
        afterCreate() {
            if (typeof window.localStorage !== "undefined") {
                self.readFromLocalStorage();
                const disposer = reaction(
                    () => self.isRightMenuPinned,
                    isRightMenuPinned => {
                        window.localStorage.setItem(STORAGE_NAME_FOR_PINNED_MENU, JSON.stringify(isRightMenuPinned))
                    }
                );
                addDisposer(self, disposer);
                const sidebarDisposer = reaction(
                    () => self.isSidebarCollapsed,
                    isSidebarCollapsed => {
                        window.localStorage.setItem(STORAGE_NAME_FOR_SIDEBAR_COLLAPSE, JSON.stringify(isSidebarCollapsed))
                    }
                );
                addDisposer(self, sidebarDisposer);
            }
        },
        resetState() {
            self.view.workspace = null;
            self.client = null;
            self.view.resetState();
            self.rightMenu = "";
            self.rightMenuWidth = 318;
            self.isRightMenuPinned = false;
            self.clusterFilter = "";
            self.isTreeCollapsed = false;
            self.isReady = false;
        },
        setClusterFilter(clusterFilter: string) {
            self.clusterFilter = clusterFilter;
        },
        setIsReady(isReady: boolean) {
            self.isReady = isReady;
        },
        setIsOnline(isOnline: boolean) {
            self.isOnline = isOnline;
        },
        setRightMenu(rightMenu: RightMenuName) {
            self.rightMenuWidth = RIGHT_BAR_DEFAULT_WIDTH;
            self.rightMenu = rightMenu;
        },
        setRightMenuWidth(rightMenuWidth: number) {
            self.rightMenuWidth = rightMenuWidth;
        },
        toggleIsTreeCollapsed(newValue: boolean = !self.isTreeCollapsed) {
            self.isTreeCollapsed = newValue;
        },
        toggleIsSidebarCollapsed(newValue: boolean = !self.isSidebarCollapsed) {
            self.isSidebarCollapsed = newValue;
        },
        toggleTheme() {
            self.darkTheme = !self.darkTheme;
        },
        setLeftMenuWidth(width: number) {
            self.leftMenuWidth = width;
        },
        setClient(client: Client) {
            self.client = client;
        },
        setWorkspaces(workspaces: WorkspaceModelSnapshotIn[]) {
            // @ts-ignore
            self.workspaces = workspaces;
        },
        setCurrentLocale(locale: string) {
            self.currentLocale = locale;
        },
    }))

export type RootStoreModel = Instance<typeof RootStore>
